import { Button, Loader } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import tvDisplay from "src/pages/Welcome/images/pairScreen/default-tv-display.svg";
import pixiDevice from "../NumberOfScreensStep/assets/pixi-device.svg";
import {
  DiscountLineItemPrice,
  LineItem,
  LineItemCaption,
  LineItemColumn,
  LineItemImage,
  LineItemImageWrap,
  LineItemPrice,
  LineItemTitle,
  OrderSummaryCardStyled,
  PurchaseError,
  PurchaseRow,
  SummaryDescription,
  SummaryDivider,
  SummaryHeader,
  SummaryHeading,
  SummaryItems,
  ShippingLineItemPrice,
  TotalRow,
  TotalValue,
} from "./styles";

export interface OrderSummaryProps {
  planName: string;
  licenses: number;
  licensesPrice: string;
  freeDevices: boolean;
  deviceCount: number;
  /**
   * Formatted zero amount (e.g. "$0") shown as the free devices' cost — mirrors
   * the Device review card (GRWBKK-1739 follow-up).
   */
  deviceCostPrice: string;
  /**
   * Formatted discount amount (positive, e.g. "$144") — rendered as a green
   * negative line under the licences price. Omitted → no discount line
   * (free-device / no-deal flows).
   */
  discountPrice?: string;
  shippingPrice: string;
  total: string;
  purchasing: boolean;
  onPurchase: () => void;
  /** Charge/SCA failure message from usePurchase (useError); empty when none. */
  error?: string;
}

export const OrderSummary = ({
  planName,
  licenses,
  licensesPrice,
  freeDevices,
  deviceCount,
  deviceCostPrice,
  discountPrice,
  shippingPrice,
  total,
  purchasing,
  onPurchase,
  error,
}: OrderSummaryProps) => (
  <OrderSummaryCardStyled data-testid="checkout-order-summary">
    <SummaryHeader>
      <SummaryHeading>
        <FormattedMessage
          id="pixi_wizard.checkout.summary.title"
          defaultMessage="Order Summary"
        />
      </SummaryHeading>
      <SummaryDescription>
        <FormattedMessage
          id="pixi_wizard.checkout.summary.description"
          defaultMessage="Review the items in your order and confirm your subscription details. Your total includes all selected licences, devices, discounts, and shipping charges."
        />
      </SummaryDescription>
    </SummaryHeader>

    <SummaryItems>
      <LineItem>
        <LineItemImageWrap>
          <LineItemImage src={tvDisplay} alt="" aria-hidden />
        </LineItemImageWrap>
        <LineItemColumn>
          <LineItemCaption>
            <FormattedMessage
              id="pixi_wizard.checkout.summary.plan_caption"
              defaultMessage="{plan} Plan"
              values={{ plan: planName }}
            />
          </LineItemCaption>
          <LineItemTitle data-testid="summary-licenses">
            <FormattedMessage
              id="pixi_wizard.checkout.summary.licenses"
              defaultMessage="x{count} {count, plural, one {Licence} other {Licences}}"
              values={{ count: licenses }}
            />
          </LineItemTitle>
          <LineItemPrice>{licensesPrice}</LineItemPrice>
          {discountPrice && (
            <DiscountLineItemPrice data-testid="summary-discount">
              -{discountPrice}
            </DiscountLineItemPrice>
          )}
        </LineItemColumn>
      </LineItem>

      {freeDevices && (
        <LineItem data-testid="summary-devices">
          <LineItemImageWrap>
            <LineItemImage src={pixiDevice} alt="" aria-hidden />
          </LineItemImageWrap>
          <LineItemColumn>
            <LineItemTitle>
              <FormattedMessage
                id="pixi_wizard.checkout.summary.free_devices"
                defaultMessage="x{count} {count, plural, one {Free PIXI Device} other {Free PIXI Devices}}"
                values={{ count: deviceCount }}
              />
            </LineItemTitle>
            <LineItemPrice data-testid="summary-device-price">
              {deviceCostPrice}
            </LineItemPrice>
            <ShippingLineItemPrice data-testid="summary-shipping">
              <FormattedMessage
                id="pixi_wizard.checkout.summary.shipping"
                defaultMessage="Shipping: +{amount}"
                values={{ amount: shippingPrice }}
              />
            </ShippingLineItemPrice>
          </LineItemColumn>
        </LineItem>
      )}

      <TotalRow>
        <LineItemColumn>
          <LineItemTitle>
            <FormattedMessage
              id="pixi_wizard.checkout.summary.total"
              defaultMessage="Total"
            />
          </LineItemTitle>
          <TotalValue data-testid="summary-total">{total}</TotalValue>
        </LineItemColumn>
      </TotalRow>
    </SummaryItems>

    <SummaryDivider />

    {error && (
      <PurchaseError data-testid="checkout-purchase-error">
        {error}
      </PurchaseError>
    )}

    <PurchaseRow>
      <Button
        primary
        onClick={onPurchase}
        disabled={purchasing}
        data-testid="checkout-purchase"
      >
        {purchasing ? (
          <Loader
            inline
            active
            size="small"
            data-testid="checkout-purchase-loader"
          />
        ) : (
          <FormattedMessage
            id="pixi_wizard.checkout.purchase"
            defaultMessage="Purchase"
          />
        )}
      </Button>
    </PurchaseRow>
  </OrderSummaryCardStyled>
);
